import { useState } from 'react';
import { useProducts } from '../hooks/useProducts';
import { colors, inputStyle } from '../lib/theme';
import type { DishProductInput } from '../types';
import IngredientAmountModal from './IngredientAmountModal';

const MAX_PRODUCTS = 10;
const MIN_QUERY_LENGTH = 3;

interface Props {
  selected: DishProductInput[];
  onChange: (products: DishProductInput[]) => void;
}

export default function ProductPicker({ selected, onChange }: Props) {
  const { products } = useProducts();
  const [query, setQuery] = useState('');
  const [editingProductId, setEditingProductId] = useState<number | null>(null);

  const selectedIds = selected.map((p) => p.productId);
  const atLimit = selected.length >= MAX_PRODUCTS;

  const trimmed = query.trim().toLowerCase();
  const matches =
    !atLimit && trimmed.length >= MIN_QUERY_LENGTH
      ? products.filter(
          (p) => !p.archived && !selectedIds.includes(p.id) && p.name.toLowerCase().includes(trimmed),
        )
      : [];

  function addProduct(id: number) {
    if (atLimit) return;
    onChange([...selected, { productId: id, amountGrams: null, calories: null }]);
    setQuery('');
    // Immediately prompt for the grams/kcal that make up this tag.
    setEditingProductId(id);
  }

  function removeProduct(id: number) {
    onChange(selected.filter((p) => p.productId !== id));
  }

  function updateProduct(next: DishProductInput) {
    onChange(selected.map((p) => (p.productId === next.productId ? next : p)));
  }

  const editingEntry = selected.find((p) => p.productId === editingProductId);
  const editingProduct = editingEntry ? products.find((p) => p.id === editingEntry.productId) : undefined;

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: selected.length ? '0.5rem' : 0 }}>
        {selected.map((entry) => {
          const product = products.find((p) => p.id === entry.productId);
          const label = product?.name ?? `Product #${entry.productId}`;
          return (
            <span
              key={entry.productId}
              style={{
                background: colors.mint,
                color: colors.mintText,
                borderRadius: 999,
                padding: '0.35rem 0.75rem',
                fontSize: '0.85rem',
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.35rem',
              }}
            >
              <button
                type="button"
                onClick={() => setEditingProductId(entry.productId)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'inherit',
                  font: 'inherit',
                  padding: 0,
                }}
              >
                {label}
                {entry.amountGrams != null ? ` ${entry.amountGrams}g` : ''}
                {entry.calories != null ? ` ${entry.calories} kcal` : ''}
                {entry.amountGrams == null && entry.calories == null ? ' · add amount' : ''}
              </button>
              <button
                type="button"
                onClick={() => removeProduct(entry.productId)}
                aria-label={`Remove ${label}`}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'inherit',
                  fontWeight: 700,
                  padding: 0,
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </span>
          );
        })}
      </div>

      <div style={{ position: 'relative' }}>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={atLimit ? `Max ${MAX_PRODUCTS} products reached` : 'Type 3+ letters to find a product…'}
          disabled={atLimit}
          style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', padding: '0.6rem 0.85rem' }}
        />
        {matches.length > 0 && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              zIndex: 10,
              background: colors.bone,
              borderRadius: 14,
              boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
              padding: '0.3rem',
              marginTop: '0.25rem',
              maxHeight: 220,
              overflowY: 'auto',
            }}
          >
            {matches.map((product) => (
              <button
                type="button"
                key={product.id}
                onClick={() => addProduct(product.id)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  background: 'none',
                  border: 'none',
                  borderRadius: 10,
                  padding: '0.5rem 0.75rem',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                }}
              >
                {product.name}{' '}
                <span style={{ opacity: 0.6 }}>· {product.caloriesPer100g} kcal/100g</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {!atLimit && (
        <div style={{ fontSize: '0.8rem', opacity: 0.65, marginTop: '0.25rem' }}>
          {selectedIds.length}/{MAX_PRODUCTS} selected
        </div>
      )}

      {editingEntry && editingProduct && (
        <IngredientAmountModal
          productName={editingProduct.name}
          caloriesPer100g={editingProduct.caloriesPer100g}
          value={editingEntry}
          onSave={updateProduct}
          onRemove={() => removeProduct(editingEntry.productId)}
          onClose={() => setEditingProductId(null)}
        />
      )}
    </div>
  );
}
